import { lazy, Suspense, useContext, type ComponentProps } from 'react'
import { useIntl } from 'react-intl'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import TextButton from 'LuxKit/Button/TextButton'
import OfferAddToCartButton from 'components/OfferPage/OfferBookButtons/OfferAddToCartButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { OfferPageBurnSelectionContext } from 'contexts/OfferPage/OfferPageBurnSelectionContext'

const OfferPageBNBLcta = lazy(() => import('components/OfferPage/OfferPageBNBLcta'))

const Actions = styled.div`
  display: grid;
  gap: 0;
  padding-inline: ${rem(16)};
  min-width: 0;
  ${mediaQueryUp.tablet} { min-width: ${rem(272)}; }
`

const menuButtonStyles = css`
  && {
    min-height: ${rem(48)};
    justify-content: flex-start;
    padding: 0;
    border: 0;
    background: transparent;
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
    text-decoration: none;
    font-size: ${rem(14)};
  }

  &&:hover {
    background: ${props => props.theme.palette.neutral.surface.lightBackground};
  }

  && svg {
    width: ${rem(24)};
    height: ${rem(24)};
  }
`

const MenuAddToCartButton = styled(OfferAddToCartButton)`${menuButtonStyles}`

export interface FlashPackageSelectionActionsProps {
  packageView?: App.OfferPackageOptionView
  requireCommittedSameRoomSelection: boolean
  canAddToCart: boolean
  canBuyNowBookLater: boolean
  onInvalidSelection: () => void
  onSubmit: () => void
  bnblPresentation?: ComponentProps<typeof OfferPageBNBLcta>['modalPresentation']
  onBeforePurchase?: () => boolean
  purchaseSessionKey?: string
  onPurchaseSessionChange?: (open: boolean) => void
  onRegisterClose?: (close: (() => void) | undefined) => void
}

/** The same capability-gated actions populate the desktop menu and mobile flow. */
function FlashPackageSelectionActions({ packageView, requireCommittedSameRoomSelection, canAddToCart, canBuyNowBookLater, onInvalidSelection, onSubmit, bnblPresentation, onBeforePurchase, purchaseSessionKey, onPurchaseSessionChange, onRegisterClose }: FlashPackageSelectionActionsProps) {
  const intl = useIntl()
  const { quote: selectionBurnQuote } = useContext(OfferPageBurnSelectionContext)
  const disabled = !!selectionBurnQuote && !selectionBurnQuote.ready
  return <Actions>
    {canAddToCart && packageView && <MenuAddToCartButton disabled={disabled} requireCommittedSameRoomSelection={requireCommittedSameRoomSelection} packageView={packageView} presentation="menu" onPurchaseComplete={onSubmit} size="small" fit="full-width" />}
    {canAddToCart && !packageView && <TextButton kind="tertiary" size="small" fit="full-width" onClick={onInvalidSelection}>{intl.formatMessage({ id: 'fFXrfICRWcu___Tem3ml', defaultMessage: 'Add to cart', description: 'Button text to add selected room options to cart in multi-room booking' })}</TextButton>}
    {canBuyNowBookLater && <Suspense fallback={null}><OfferPageBNBLcta disabled={disabled} packageView={packageView} presentation="menu" modalPresentation={bnblPresentation} onBeforePurchase={onBeforePurchase} purchaseSessionKey={purchaseSessionKey} onPurchaseSessionChange={onPurchaseSessionChange} onRegisterClose={onRegisterClose} /></Suspense>}
  </Actions>
}

export default FlashPackageSelectionActions
